import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import Quotations from "@/pages/Quotations";
import Customers, { CustomerDetail } from "@/pages/Customers";
import LiveJobs from "@/pages/LiveJobs";
import JobDetail from "@/pages/JobDetail";
import Board from "@/pages/Board";
import Clock from "@/pages/Clock";
import TimeLabour from "@/pages/TimeLabour";
import Purchasing from "@/pages/Purchasing";
import ReadyToInvoice from "@/pages/ReadyToInvoice";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import PersonDetail from "@/pages/PersonDetail";
import LeaveCalendar from "@/pages/LeaveCalendar";
import { MyLeave, MyProfile, MyCerts, LeaveRequests, WhosOff, People, CertsExpiring, Apprentices } from "@/pages/HR";

const OFFICE = ["supervisor", "office", "management", "support"];
const COST = ["office", "supervisor", "management", "support"];
const ADMIN = ["management", "support", "office"];

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "shop_floor" ? "/clock" : "/dashboard"} replace />;
}

const wrap = (el, roles) => (
  <ProtectedRoute roles={roles}>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors theme={localStorage.getItem("mk-theme") === "light" ? "light" : "dark"} />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/dashboard" element={wrap(<Dashboard />, OFFICE)} />
            <Route path="/quotations" element={wrap(<Quotations />, COST)} />
            <Route path="/customers" element={wrap(<Customers />, OFFICE)} />
            <Route path="/customers/:id" element={wrap(<CustomerDetail />, OFFICE)} />
            <Route path="/jobs" element={wrap(<Jobs />, OFFICE)} />
            <Route path="/live-jobs" element={wrap(<LiveJobs />, OFFICE)} />
            <Route path="/jobs/completed" element={wrap(<Jobs completed />, OFFICE)} />
            <Route path="/jobs/:id" element={wrap(<JobDetail />)} />
            <Route path="/board" element={wrap(<Board />)} />
            <Route path="/clock" element={wrap(<Clock />)} />
            <Route path="/time" element={wrap(<TimeLabour />, OFFICE)} />
            <Route path="/purchasing" element={wrap(<Purchasing />, COST)} />
            <Route path="/ready-to-invoice" element={wrap(<ReadyToInvoice />, COST)} />
            <Route path="/invoices" element={wrap(<Invoices />, COST)} />
            <Route path="/reports" element={wrap(<Reports />, OFFICE)} />
            <Route path="/settings" element={wrap(<Settings />, ADMIN)} />
            <Route path="/audit" element={wrap(<Audit />, OFFICE)} />
            <Route path="/hr/leave" element={wrap(<MyLeave />)} />
            <Route path="/hr/profile" element={wrap(<MyProfile />)} />
            <Route path="/hr/certs" element={wrap(<MyCerts />)} />
            <Route path="/hr/calendar" element={wrap(<LeaveCalendar />, COST)} />
            <Route path="/hr/whos-off" element={wrap(<WhosOff />, OFFICE)} />
            <Route path="/hr/requests" element={wrap(<LeaveRequests />, COST)} />
            <Route path="/hr/people" element={wrap(<People />, COST)} />
            <Route path="/hr/people/:id" element={wrap(<PersonDetail />, COST)} />
            <Route path="/hr/certs-expiring" element={wrap(<CertsExpiring />, COST)} />
            <Route path="/hr/apprentices" element={wrap(<Apprentices />, COST)} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
